/**
 * Google Tag Manager (GTM) Utility
 * 
 * This file contains helper functions to push events to the GTM data layer.
 * All user actions are tracked here for analytics and conversion tracking.
 */

/**
 * Push an event to the GTM data layer
 * @param {object} eventData - The event data to push
 */
const pushToDataLayer = (eventData) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push(eventData);
    console.log('📊 GTM Event:', eventData);
  } else {
    console.warn('⚠️ GTM dataLayer not available');
  }
};

/**
 * Track page view
 * @param {string} pagePath - The page path
 * @param {string} pageTitle - The page title
 */
export const trackPageView = (pagePath, pageTitle) => {
  pushToDataLayer({
    event: 'page_view',
    page_path: pagePath,
    page_title: pageTitle,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track when user performs initial search (pre-search)
 * @param {string} selectorType - 'email' or 'phone'
 * @param {number} resultsCount - Number of results found
 */
export const trackSearch = (selectorType, resultsCount) => {
  pushToDataLayer({
    event: 'search_initiated',
    selector_type: selectorType,
    results_count: resultsCount,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track when user selects a tier (begins checkout)
 * @param {string} tier - 'basic' or 'deepdive'
 * @param {string} selectorType - 'email' or 'phone'
 * @param {number} resultsCount - Number of pre-search results
 */
export const trackTierSelected = (tier, selectorType, resultsCount) => {
  pushToDataLayer({
    event: 'tier_selected',
    tier: tier,
    selector_type: selectorType,
    results_count: resultsCount,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track when user is redirected to Stripe checkout
 * @param {string} reportId - The report ID
 * @param {string} tier - 'tier1' or 'tier2'
 * @param {string} selectorType - 'email' or 'phone'
 */
export const trackCheckoutInitiated = (reportId, tier, selectorType) => {
  pushToDataLayer({
    event: 'begin_checkout',
    report_id: reportId,
    tier: tier,
    selector_type: selectorType,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track successful payment
 * @param {string} reportId - The report ID
 * @param {string} tier - 'tier1' or 'tier2'
 * @param {string} selectorType - 'email' or 'phone'
 * @param {number} amount - Amount paid in cents
 */
export const trackPurchase = (reportId, tier, selectorType, amount) => {
  const value = amount / 100; // Convert cents to dollars
  
  pushToDataLayer({
    event: 'purchase',
    transaction_id: reportId,
    value: value,
    currency: 'USD',
    tier: tier,
    selector_type: selectorType,
    timestamp: new Date().toISOString(),
    // Enhanced ecommerce data
    ecommerce: {
      transaction_id: reportId,
      value: value,
      currency: 'USD',
      items: [{
        item_id: tier,
        item_name: tier === 'tier1' ? 'Basic Check' : 'Deep Dive Report',
        price: value,
        quantity: 1,
      }]
    }
  });
};

/**
 * Track payment cancellation
 * @param {string} selectorType - 'email' or 'phone'
 */
export const trackPaymentCancelled = (selectorType) => {
  pushToDataLayer({
    event: 'payment_cancelled',
    selector_type: selectorType,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track when user views their report
 * @param {string} reportId - The report ID
 * @param {string} tier - 'tier1' or 'tier2'
 * @param {number} resultsCount - Number of results in report
 */
export const trackReportViewed = (reportId, tier, resultsCount) => {
  pushToDataLayer({
    event: 'report_viewed',
    report_id: reportId,
    tier: tier,
    results_count: resultsCount,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track when user requests an upgrade
 * @param {string} reportId - The report ID
 * @param {string} fromTier - Current tier
 * @param {string} toTier - Target tier
 */
export const trackUpgradeInitiated = (reportId, fromTier, toTier) => {
  pushToDataLayer({
    event: 'upgrade_initiated',
    report_id: reportId,
    from_tier: fromTier,
    to_tier: toTier,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track errors
 * @param {string} errorType - Type of error
 * @param {string} errorMessage - Error message
 * @param {string} location - Where the error occurred
 */
export const trackError = (errorType, errorMessage, location) => {
  pushToDataLayer({
    event: 'error_occurred',
    error_type: errorType,
    error_message: errorMessage,
    location: location,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track user drop-off at specific funnel stages
 * @param {string} stage - Funnel stage (e.g., 'search', 'tier_selection', 'checkout')
 * @param {string} action - What happened (e.g., 'abandoned', 'back_button', 'closed_tab')
 */
export const trackDropOff = (stage, action) => {
  pushToDataLayer({
    event: 'funnel_drop_off',
    funnel_stage: stage,
    drop_off_action: action,
    timestamp: new Date().toISOString(),
  });
};

export default {
  trackPageView,
  trackSearch,
  trackTierSelected,
  trackCheckoutInitiated,
  trackPurchase,
  trackPaymentCancelled,
  trackReportViewed,
  trackUpgradeInitiated,
  trackError,
  trackDropOff,
};

